import { Megaphone } from 'lucide-react';

import { SurfaceSidebarNavItemLink, type SurfaceSidebarTone } from '@/components/layout/SurfaceSidebar';
import { Badge } from '@/components/ui/badge';
import { FeatureStageBadge, type FeatureReleaseStage } from '@/components/ui/feature-stage-badge';
import { useAdvisoriesShell, useMspAdvisoriesShell } from '@/hooks/useAdvisoriesShell';
import { useVendorAdvisoriesShell } from '@/hooks/useVendorAdvisoriesShell';
import { useVendor } from '@/contexts/VendorContext';

export interface AdvisoriesNavItemProps {
  audience: 'customer' | 'vendor' | 'msp';
  tone: SurfaceSidebarTone;
  collapsed: boolean;
  collapsedNavItemClass?: string;
  onClick?: () => void;
}

/** ENT-6091: each menu reads its own context's server exposure and unread count.
 * Keep the item hidden until that context resolves enabled.
 */
export function AdvisoriesNavItem(props: AdvisoriesNavItemProps) {
  return props.audience === 'vendor' ? <VendorAdvisoriesNavItem {...props} /> : props.audience === 'msp' ? <MspAdvisoriesNavItem {...props} /> : <CustomerAdvisoriesNavItem {...props} />;
}

export function MspAdvisoriesNavItem(props: AdvisoriesNavItemProps) {
  const shell = useMspAdvisoriesShell();
  if (shell.loading || !shell.enabled) return null;
  return <AdvisoriesNavLink {...props} audience="msp" unread={shell.unread} releaseStage={shell.releaseStage} />;
}

function CustomerAdvisoriesNavItem(props: AdvisoriesNavItemProps) {
  const shell = useAdvisoriesShell();
  if (shell.loading || !shell.enabled) return null;
  return <AdvisoriesNavLink {...props} unread={shell.unread} releaseStage={shell.releaseStage} />;
}

function VendorAdvisoriesNavItem(props: AdvisoriesNavItemProps) {
  const shell = useVendorAdvisoriesShell();
  const { requestedAdvisoryProfileId } = useVendor();
  if (shell.loading || !shell.enabled) return null;
  return <AdvisoriesNavLink {...props} unread={shell.unread} releaseStage={shell.releaseStage} profileId={requestedAdvisoryProfileId} />;
}

function AdvisoriesNavLink({
  audience,
  tone,
  collapsed,
  collapsedNavItemClass,
  onClick,
  unread,
  releaseStage,
  profileId,
}: AdvisoriesNavItemProps & { unread: number; releaseStage: FeatureReleaseStage; profileId?: string | null }) {
  const label = `${unread} unread ${unread === 1 ? 'advisory' : 'advisories'}`;
  const to = audience === 'vendor' ? `/vendor/advisories${profileId ? `?${new URLSearchParams({ profile: profileId })}` : ''}` : audience === 'msp' ? '/msp/advisories' : '/advisories';

  return (
    <SurfaceSidebarNavItemLink
      tone={tone}
      to={to}
      title="Advisories"
      icon={Megaphone}
      collapsed={collapsed}
      collapsedNavItemClass={collapsedNavItemClass}
      onClick={onClick}
      trailingAccessory={releaseStage || unread > 0 ? (
        <span className="flex items-center gap-1.5">
          <FeatureStageBadge
            releaseStage={releaseStage}
            presentation="sidebar"
          />
          {unread > 0 ? (
            <Badge
              variant="destructive"
              aria-label={label}
              data-testid="advisories-unread-badge"
              className="h-5 min-w-5 justify-center rounded-full border-0 px-1.5 text-[11px] font-semibold tabular-nums"
            >
              {unread > 99 ? '99+' : unread}
            </Badge>
          ) : null}
        </span>
      ) : undefined}
      collapsedAccessory={unread > 0 ? (
        <span
          aria-label={label}
          className="flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-semibold leading-none text-destructive-foreground tabular-nums"
        >
          {unread > 9 ? '9+' : unread}
        </span>
      ) : undefined}
    />
  );
}
